import React, { useEffect, useMemo } from 'react';
import Toggle from 'src/components/Toggle/Toggle';
import CategoryPill from 'src/components/CategoryPill/CategoryPill';
import Callout from 'src/components/InformationCallout/Callout';
import CustomPagination from 'src/components/CustomPagination/CustomPagination';
import { useClientPagination } from 'src/components/CustomPagination/useClientPagination';
import { IServiceRequest } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import './step-review-visibility.scss';

interface StepReviewVisibilityProps {
  stagedServiceRequests: IServiceRequest[];
  unchangedServiceRequests: IServiceRequest[];
  targetIsInternalOnly: boolean;
  willBecomeInternalCount: number;
  willBecomeExternalCount: number;
  isSubmitting: boolean;
  error: string | null;
  onSetTarget: (isInternalOnly: boolean) => void;
}

interface StagedServiceRequestRowProps {
  serviceRequest: IServiceRequest;
  isUnchanged: boolean;
  targetIsInternalOnly: boolean;
}

const StagedServiceRequestRow = React.memo(
  ({
    serviceRequest,
    isUnchanged,
    targetIsInternalOnly,
  }: StagedServiceRequestRowProps) => {
    const ticketNumber: string =
      serviceRequest.ticketNumber ?? serviceRequest.number ?? '—';
    const subject: string = serviceRequest.subject?.trim() || 'No subject';

    const rowClassName: string = isUnchanged
      ? 'rsr-row rsr-row-unchanged'
      : 'rsr-row';

    const unchangedLabel: string = targetIsInternalOnly
      ? 'Already internal — no change'
      : 'Already external — no change';

    return (
      <div className={rowClassName}>
        <div className="rsr-row-identity">
          <div className="rsr-row-number">{ticketNumber}</div>
          <div className="rsr-row-subject">{subject}</div>
          {isUnchanged && (
            <div className="rsr-row-unchanged-label">{unchangedLabel}</div>
          )}
        </div>
        <div className="rsr-row-status">
          {serviceRequest.status && (
            <CategoryPill text={serviceRequest.status} size="small" />
          )}
        </div>
      </div>
    );
  }
);

StagedServiceRequestRow.displayName = 'StagedServiceRequestRow';

const StepReviewVisibility = ({
  stagedServiceRequests,
  unchangedServiceRequests,
  targetIsInternalOnly,
  willBecomeInternalCount,
  willBecomeExternalCount,
  isSubmitting,
  error,
  onSetTarget,
}: StepReviewVisibilityProps) => {
  const totalStagedCount: number = stagedServiceRequests.length;
  const unchangedCount: number = unchangedServiceRequests.length;
  const showEmpty: boolean = totalStagedCount === 0;

  const {
    currentPage,
    totalPages,
    paginatedItems,
    setCurrentPage,
    resetToFirstPage,
  } = useClientPagination<IServiceRequest>({
    items: stagedServiceRequests,
    itemsPerPage: 12,
  });

  useEffect(() => {
    resetToFirstPage();
  }, [totalStagedCount]);

  const showPagination: boolean = totalPages > 1;

  const handleBulkToggle = (currentValue: boolean): void => {
    if (isSubmitting || totalStagedCount === 0) {
      return;
    }

    onSetTarget(!currentValue);
  };

  const externalEndClassName: string = targetIsInternalOnly
    ? 'rsr-bulk-toggle-end rsr-bulk-toggle-end-external'
    : 'rsr-bulk-toggle-end rsr-bulk-toggle-end-external rsr-bulk-toggle-end-active';

  const internalEndClassName: string = targetIsInternalOnly
    ? 'rsr-bulk-toggle-end rsr-bulk-toggle-end-internal rsr-bulk-toggle-end-active'
    : 'rsr-bulk-toggle-end rsr-bulk-toggle-end-internal';

  const listClassName: string = targetIsInternalOnly
    ? 'rsr-list rsr-list-internal'
    : 'rsr-list rsr-list-external';

  const unchangedIds: Set<string> = useMemo(() => {
    return new Set(
      unchangedServiceRequests.map((sr: IServiceRequest) => sr.id)
    );
  }, [unchangedServiceRequests]);

  return (
    <div className="rsr-step">
      <div className="rsr-header">
        <div className="rsr-summary">
          <div className="rsr-summary-pill rsr-summary-pill-internal">
            <span className="rsr-summary-pill-count">
              {willBecomeInternalCount}
            </span>
            <span className="rsr-summary-pill-label">will become internal</span>
          </div>
          <div className="rsr-summary-pill rsr-summary-pill-external">
            <span className="rsr-summary-pill-count">
              {willBecomeExternalCount}
            </span>
            <span className="rsr-summary-pill-label">will become external</span>
          </div>
          <div className="rsr-summary-pill rsr-summary-pill-unchanged">
            <span className="rsr-summary-pill-count">{unchangedCount}</span>
            <span className="rsr-summary-pill-label">unchanged</span>
          </div>
        </div>
        <div className="rsr-bulk-toggle">
          <span className={externalEndClassName}>External</span>
          <Toggle
            label=""
            value={targetIsInternalOnly}
            onToggle={handleBulkToggle}
          />
          <span className={internalEndClassName}>Internal</span>
        </div>
        <Callout
          variant="info"
          title="One toggle sets visibility for every request below."
          subtitle="Rows already in the target state are marked and won't change."
        />
      </div>
      <div className={listClassName}>
        {showEmpty && (
          <div className="rsr-state">
            <div className="rsr-state-heading">Nothing staged</div>
            <div className="rsr-state-body">
              Go back and pick service requests to manage.
            </div>
          </div>
        )}
        {!showEmpty &&
          paginatedItems.map((sr: IServiceRequest) => {
            const isUnchanged: boolean = unchangedIds.has(sr.id);

            return (
              <StagedServiceRequestRow
                key={sr.id}
                serviceRequest={sr}
                isUnchanged={isUnchanged}
                targetIsInternalOnly={targetIsInternalOnly}
              />
            );
          })}
      </div>
      {showPagination && (
        <div className="rsr-pagination">
          <CustomPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            disabled={isSubmitting}
          />
        </div>
      )}
      {error && (
        <div className="rsr-error">
          <div className="rsr-error-heading">
            We couldn't apply your changes
          </div>
          <div className="rsr-error-body">{error}</div>
        </div>
      )}
    </div>
  );
};

export default StepReviewVisibility;